import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FloppyDisk } from "@phosphor-icons/react";
import {
  getEntitySettings, putEntitySettings, validateEntitySettings, buildEntityPayload,
  normalizeIdentifier,
} from "../../../lib/legalAdmin";

const TEXT_FIELDS = [
  { key: "legal_company_name", label: "Legal company name", required: true },
  { key: "registered_office_address", label: "Registered office address", required: true, textarea: true },
  { key: "legal_support_email", label: "Legal / support email", required: true },
];
const ID_FIELDS = [
  { key: "pan", label: "PAN", required: true },
  { key: "cin", label: "CIN", required: true },
  { key: "gstin", label: "GSTIN", required: false, hint: "Leave blank to clear." },
];

const EMPTY = {
  legal_company_name: "", pan: "", cin: "", registered_office_address: "",
  gstin: "", legal_support_email: "",
};

// M2 Slice 9 — Legal Entity Settings. Rendered ABOVE pricing on the Pricing Settings page; loads
// and saves fully independently of pricing (full PUT, last-valid-save-wins, no token/PATCH/audit).
export default function LegalEntitySettingsCard() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const load = async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const data = await getEntitySettings();
      setForm({
        legal_company_name: data.legal_company_name || "",
        pan: data.pan || "",
        cin: data.cin || "",
        registered_office_address: data.registered_office_address || "",
        gstin: data.gstin || "",
        legal_support_email: data.legal_support_email || "",
      });
    } catch (e) {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  // Uppercase-normalise Indian identifiers on input (matches backend).
  const setId = (k, v) => setForm((f) => ({ ...f, [k]: normalizeIdentifier(v) }));

  const save = async () => {
    const { ok, errors: errs } = validateEntitySettings(form);
    setErrors(errs);
    if (!ok) { toast.error("Please fix the highlighted fields."); return; }
    setSaving(true);
    try {
      const data = await putEntitySettings(buildEntityPayload(form));
      setForm({
        legal_company_name: data.legal_company_name || "",
        pan: data.pan || "",
        cin: data.cin || "",
        registered_office_address: data.registered_office_address || "",
        gstin: data.gstin || "",
        legal_support_email: data.legal_support_email || "",
      });
      setErrors({});
      toast.success("Legal Entity Settings saved.");
    } catch (e) {
      toast.error(e.displayMessage || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid-card p-5" data-testid="legal-entity-settings-section">
      <div className="overline mb-1">LEGAL ENTITY SETTINGS</div>
      <p className="text-xs text-[hsl(var(--muted))] mb-4">
        Applies to newly generated legal documents. Full save — every field is submitted.
      </p>

      {loading ? (
        <div className="mono text-sm" data-testid="legal-entity-loading">Loading…</div>
      ) : loadError ? (
        <div className="flex items-center justify-between gap-4" data-testid="legal-entity-load-error">
          <p className="text-sm">Couldn’t load Legal Entity Settings.</p>
          <button className="btn-ghost shrink-0" onClick={load} data-testid="legal-entity-retry">Retry</button>
        </div>
      ) : (
        <div className="space-y-4" data-testid="legal-entity-form">
          {TEXT_FIELDS.map((f) => (
            <label key={f.key} htmlFor={`les-${f.key}`} className="block">
              <div className="overline mb-1.5">{f.label}{f.required && <span className="text-[hsl(var(--danger))]"> *</span>}</div>
              {f.textarea ? (
                <textarea id={`les-${f.key}`} className="input-field" rows={2}
                  value={form[f.key]} onChange={(e) => setField(f.key, e.target.value)}
                  aria-describedby={errors[f.key] ? `les-${f.key}-err` : undefined}
                  data-testid={`legal-entity-${f.key}`} />
              ) : (
                <input id={`les-${f.key}`} className="input-field"
                  value={form[f.key]} onChange={(e) => setField(f.key, e.target.value)}
                  aria-describedby={errors[f.key] ? `les-${f.key}-err` : undefined}
                  data-testid={`legal-entity-${f.key}`} />
              )}
              {errors[f.key] && <p id={`les-${f.key}-err`} className="text-xs text-[hsl(var(--danger))] mt-1">{errors[f.key]}</p>}
            </label>
          ))}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {ID_FIELDS.map((f) => (
              <label key={f.key} htmlFor={`les-${f.key}`} className="block">
                <div className="overline mb-1.5">{f.label}{f.required && <span className="text-[hsl(var(--danger))]"> *</span>}</div>
                <input id={`les-${f.key}`} className="input-field uppercase"
                  value={form[f.key]} onChange={(e) => setId(f.key, e.target.value)}
                  aria-describedby={errors[f.key] ? `les-${f.key}-err` : undefined}
                  data-testid={`legal-entity-${f.key}`} />
                {f.hint && !errors[f.key] && <p className="text-[11px] text-[hsl(var(--muted))] mt-1">{f.hint}</p>}
                {errors[f.key] && <p id={`les-${f.key}-err`} className="text-xs text-[hsl(var(--danger))] mt-1">{errors[f.key]}</p>}
              </label>
            ))}
          </div>
          <div className="flex justify-end">
            <button className="btn-primary inline-flex items-center gap-2" disabled={saving}
              onClick={save} data-testid="legal-entity-save-btn" aria-live="polite">
              <FloppyDisk size={16} /> {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
